// src/PublicNavbar.js
// Updated with internationalization (i18n) support

import React from "react";
import { Link as RouterLink } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  Box,
  useTheme,
  useMediaQuery,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  ListItemIcon,
  Divider,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import DashboardIcon from "@mui/icons-material/Dashboard";
import PeopleIcon from "@mui/icons-material/People";
import ListAltIcon from "@mui/icons-material/ListAlt";
import FindInPageIcon from "@mui/icons-material/FindInPage";
import AssessmentIcon from "@mui/icons-material/Assessment";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import StarIcon from "@mui/icons-material/Star";
import { auth } from "./firebaseConfig";
import { useAuth } from "./AuthContext";
import LanguageSwitcher from "./components/LanguageSwitcher";

function PublicNavbar() {
  const { currentUser, userRole } = useAuth();
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [drawerOpen, setDrawerOpen] = React.useState(false);

  const toggleDrawer = (open) => () => {
    setDrawerOpen(open);
  };

  const handleLogout = () => {
    auth.signOut().then(() => {
      window.location.href = "/";
    });
  };

  // Navigation items with translation keys
  const navItems = {
    admin: [
      {
        text: t("nav.dashboard"),
        to: "/app/dashboard",
        icon: <DashboardIcon />,
      },
      {
        text: t("nav.userManagement"),
        to: "/app/user-management",
        icon: <PeopleIcon />,
      },
      {
        text: t("nav.customLists"),
        to: "/app/custom-lists",
        icon: <ListAltIcon />,
      },
      {
        text: t("nav.findTest"),
        to: "/app/find-test",
        icon: <FindInPageIcon />,
      },
      { text: t("nav.reports"), to: "/app/reports", icon: <AssessmentIcon /> },
      { text: t("nav.support"), to: "/app/support", icon: <HelpOutlineIcon /> },
    ],
    pro_user: [
      {
        text: t("nav.dashboard"),
        to: "/app/dashboard",
        icon: <DashboardIcon />,
      },
      {
        text: t("nav.customLists"),
        to: "/app/custom-lists",
        icon: <ListAltIcon />,
      },
      {
        text: t("nav.findTest"),
        to: "/app/find-test",
        icon: <FindInPageIcon />,
      },
      { text: t("nav.reports"), to: "/app/reports", icon: <AssessmentIcon /> },
      { text: t("nav.support"), to: "/app/support", icon: <HelpOutlineIcon /> },
    ],
    superuser: [],
    user: [
      {
        text: t("nav.dashboard"),
        to: "/app/dashboard",
        icon: <DashboardIcon />,
      },
      {
        text: t("nav.myTestHistory"),
        to: "/app/find-test",
        icon: <FindInPageIcon />,
      },
      { text: t("nav.reports"), to: "/app/reports", icon: <AssessmentIcon /> },
      {
        text: t("nav.profile"),
        to: "/app/profile",
        icon: <AccountCircleIcon />,
      },
      { text: t("nav.support"), to: "/app/support", icon: <HelpOutlineIcon /> },
    ],
  };

  // Determine which navigation links to show based on role
  let appLinks = [];
  if (currentUser) {
    if (userRole === "admin" || userRole === "superuser") {
      appLinks = [
        ...navItems.admin,
        ...(userRole === "superuser" ? navItems.superuser : []),
      ];
    } else if (userRole === "pro_user") {
      appLinks = navItems.pro_user;
    } else if (userRole === "user") {
      appLinks = navItems.user;
    }
  }

  return (
    <AppBar position="fixed">
      <Toolbar>
        <Box
          component={RouterLink}
          to="/"
          sx={{
            flexGrow: 1,
            display: "flex",
            alignItems: "center",
            textDecoration: "none",
            color: "inherit",
          }}
        >
          <img
            src="/assets/images/kbquick_full_rev.svg"
            alt="KB Quick Logo"
            style={{ height: "52px", marginRight: "16px" }}
          />
          <Typography variant="h6" component="div"></Typography>
        </Box>
        {isMobile ? (
          <>
            <LanguageSwitcher variant="navbar" />
            <IconButton
              color="inherit"
              edge="end"
              onClick={toggleDrawer(true)}
              sx={{ ml: 1 }}
            >
              <MenuIcon />
            </IconButton>
          </>
        ) : (
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <Button color="inherit" component={RouterLink} to="/">
              {t("nav.home")}
            </Button>
            <Button color="inherit" component={RouterLink} to="/about">
              {t("nav.about")}
            </Button>
            <Button color="inherit" component={RouterLink} to="/compare">
              {t("nav.compare")}
            </Button>
            <Button color="inherit" component={RouterLink} to="/contact">
              {t("nav.contact")}
            </Button>

            <LanguageSwitcher variant="navbar" />

            {currentUser ? (
              <Button
                variant="outlined"
                color="inherit"
                onClick={handleLogout}
                sx={{ ml: 2 }}
              >
                {t("nav.logout")}
              </Button>
            ) : (
              <Button
                variant="outlined"
                color="inherit"
                component={RouterLink}
                to="/login"
                sx={{ ml: 2 }}
              >
                {t("nav.login")}
              </Button>
            )}
          </Box>
        )}
      </Toolbar>
      <Drawer anchor="left" open={drawerOpen} onClose={toggleDrawer(false)}>
        <Box
          sx={{ width: 250 }}
          role="presentation"
          onClick={toggleDrawer(false)}
          onKeyDown={toggleDrawer(false)}
        >
          <List>
            {/* Language switcher at top of drawer */}
            <Box onClick={(e) => e.stopPropagation()}>
              <LanguageSwitcher variant="drawer" />
            </Box>
            <Divider sx={{ my: 1 }} />

            {/* Public links when logged out */}
            {!currentUser && (
              <>
                <ListItem disablePadding>
                  <ListItemButton component={RouterLink} to="/">
                    <ListItemText primary={t("nav.home")} />
                  </ListItemButton>
                </ListItem>
                <ListItem disablePadding>
                  <ListItemButton component={RouterLink} to="/about">
                    <ListItemText primary={t("nav.about")} />
                  </ListItemButton>
                </ListItem>
                <ListItem disablePadding>
                  <ListItemButton component={RouterLink} to="/compare">
                    <ListItemText primary={t("nav.compare")} />
                  </ListItemButton>
                </ListItem>
                <ListItem disablePadding>
                  <ListItemButton component={RouterLink} to="/contact">
                    <ListItemText primary={t("nav.contact")} />
                  </ListItemButton>
                </ListItem>
                <ListItem disablePadding>
                  <ListItemButton component={RouterLink} to="/login">
                    <ListItemText primary={t("nav.login")} />
                  </ListItemButton>
                </ListItem>
              </>
            )}

            {/* Logged-in links - show ALL app navigation */}
            {currentUser && (
              <>
                {appLinks.map((item) => (
                  <ListItem key={item.to} disablePadding>
                    <ListItemButton component={RouterLink} to={item.to}>
                      <ListItemIcon sx={{ minWidth: 40 }}>
                        {item.icon}
                      </ListItemIcon>
                      <ListItemText primary={item.text} />
                    </ListItemButton>
                  </ListItem>
                ))}

                <Divider sx={{ my: 1 }} />

                {/* Beta Hub link */}
                <ListItem disablePadding>
                  <ListItemButton
                    component={RouterLink}
                    to="/app/beta"
                    sx={{
                      backgroundColor: "warning.light",
                      "&:hover": {
                        backgroundColor: "warning.main",
                      },
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: 40 }}>
                      <StarIcon />
                    </ListItemIcon>
                    <ListItemText primary={t("nav.betaHub")} />
                  </ListItemButton>
                </ListItem>

                <Divider sx={{ my: 1 }} />

                {/* Logout button */}
                <ListItem disablePadding>
                  <ListItemButton onClick={handleLogout}>
                    <ListItemText primary={t("nav.logout")} />
                  </ListItemButton>
                </ListItem>
              </>
            )}
          </List>
        </Box>
      </Drawer>
    </AppBar>
  );
}

export default PublicNavbar;
